/**
 * URL sanitising. Back-office URLs carry operator input: the CRM writes its
 * free-text table search into `?search=`, and that search is how a merchant
 * looks up a customer, so raw URLs would ship end-customers' email addresses
 * to PostHog on every autocaptured click.
 */

type Props = Record<string, unknown>;

/** Today's sanitiser (profile unset): these four keys, everything after `?` or `#` cut. */
export const LEGACY_URL_PROPERTIES = ['$current_url', '$referrer', '$initial_current_url', '$initial_referrer'] as const;

/** The extended sanitiser of every profile (spec J). */
export const URL_PROPERTIES = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$session_entry_url',
  '$session_entry_referrer',
  '$external_click_url',
  '$prev_pageview_pathname',
] as const;

/** Click ids and UTM parameters the acquisition funnels need (spec WP2). */
export const ACQUISITION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'ttclid',
  'msclkid',
  'twclid',
  'tgd_tm',
  'ref',
  'partner',
] as const;

export function stripUrlQuery(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const cut = value.search(/[?#]/);
  return cut === -1 ? value : value.slice(0, cut);
}

/** Drops the fragment and every query parameter whose name is not in `keepParams`. */
export function sanitizeUrl(value: string, keepParams: readonly string[]): string {
  const hash = value.indexOf('#');
  const noHash = hash === -1 ? value : value.slice(0, hash);
  const q = noHash.indexOf('?');
  if (q === -1) return noHash;
  const base = noHash.slice(0, q);
  if (keepParams.length === 0) return base;
  const kept = noHash
    .slice(q + 1)
    .split('&')
    .filter((pair) => {
      const rawKey = pair.split('=')[0] ?? '';
      let key = rawKey;
      try {
        key = decodeURIComponent(rawKey.replace(/\+/g, ' '));
      } catch {
        // Malformed escape: compare the raw key.
      }
      return keepParams.includes(key);
    });
  return kept.length ? `${base}?${kept.join('&')}` : base;
}

const CHAIN_HREF = /((?:attr__href|href)=")([^"]*)(")/g;

function sanitizeValue(value: unknown, keepParams: readonly string[]): unknown {
  return typeof value === 'string' ? sanitizeUrl(value, keepParams) : value;
}

function sanitizeUrlKeys(obj: unknown, keepParams: readonly string[]): unknown {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return obj;
  const out: Props = { ...(obj as Props) };
  for (const key of URL_PROPERTIES) {
    if (key in out) out[key] = sanitizeValue(out[key], keepParams);
  }
  return out;
}

/** `$web_vitals` carries one object per metric (`$web_vitals_LCP_event`…), each with its own URLs. */
const WEB_VITALS_EVENT = /^\$web_vitals_[A-Za-z]+_event$/;

function sanitizeWebVital(metric: unknown, keepParams: readonly string[]): unknown {
  if (typeof metric !== 'object' || metric === null || Array.isArray(metric)) return metric;
  const out: Props = { ...(metric as Props) };
  for (const key of ['$current_url', 'navigationURL']) {
    if (key in out) out[key] = sanitizeValue(out[key], keepParams);
  }
  const attribution = out['attribution'];
  if (typeof attribution === 'object' && attribution !== null && 'url' in attribution) {
    out['attribution'] = { ...(attribution as Props), url: sanitizeValue((attribution as Props)['url'], keepParams) };
  }
  return out;
}

/**
 * The extended sanitiser (spec J): the URL keys, their copies nested in
 * `$set`/`$set_once`, `attr__href`/`href` inside `$elements_chain`,
 * `attr__href` of `$elements`, and the URLs of each `$web_vitals_*_event`.
 * Returns a new object.
 */
export function sanitizeUrlProperties<T extends Props>(props: T, keepParams: readonly string[]): T {
  const out = sanitizeUrlKeys(props, keepParams) as Props;
  for (const key of Object.keys(out)) {
    if (WEB_VITALS_EVENT.test(key)) out[key] = sanitizeWebVital(out[key], keepParams);
  }
  if ('$set' in out) out['$set'] = sanitizeUrlKeys(out['$set'], keepParams);
  if ('$set_once' in out) out['$set_once'] = sanitizeUrlKeys(out['$set_once'], keepParams);
  if (typeof out['$elements_chain'] === 'string') {
    out['$elements_chain'] = out['$elements_chain'].replace(
      CHAIN_HREF,
      (_m, open: string, url: string, close: string) => `${open}${sanitizeUrl(url, keepParams)}${close}`,
    );
  }
  if (Array.isArray(out['$elements'])) {
    out['$elements'] = out['$elements'].map((el: unknown) =>
      typeof el === 'object' && el !== null && typeof (el as Props)['attr__href'] === 'string'
        ? { ...(el as Props), attr__href: sanitizeUrl((el as Props)['attr__href'] as string, keepParams) }
        : el,
    );
  }
  return out as T;
}
